import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import CrmConnectorCapabilityList from './CrmConnectorCapabilityList';
import { CRM_CONNECTOR_CAPABILITIES } from '../../constants/crmConnector';
import type { CrmConnectorCapabilityField } from '../../constants/crmConnector';

type CapabilityValues = Record<CrmConnectorCapabilityField, boolean>;

interface CrmConnectorActionsDialogProps {
  open: boolean;
  accountName: string;
  current: CapabilityValues;
  onClose: () => void;
  onSave: (values: CapabilityValues) => Promise<void>;
}

export default function CrmConnectorActionsDialog({
  open,
  accountName,
  current,
  onClose,
  onSave,
}: CrmConnectorActionsDialogProps) {
  const [values, setValues] = useState<CapabilityValues>(current);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const changed = CRM_CONNECTOR_CAPABILITIES.some(({ field }) => values[field] !== current[field]);

  const handleDialogClose = (_event: object, reason: string) => {
    if (reason === 'backdropClick' || submitting) return;
    onClose();
  };

  const handleToggle = (field: CrmConnectorCapabilityField) => {
    setValues(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSave = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await onSave(values);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save the supported actions');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth>
      <DialogTitle>Supported actions</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <DialogContentText variant="body2">
          Which optional actions has <strong>{accountName}</strong> implemented?
        </DialogContentText>
        <Box sx={{ mt: 2 }}>
          <CrmConnectorCapabilityList values={values} onToggle={handleToggle} disabled={submitting} />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={submitting || !changed}
          sx={{ backgroundColor: '#465fff', '&:hover': { backgroundColor: '#3641f5' } }}
        >
          {submitting ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
